import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { getFirestore, doc, getDoc } from 'firebase/firestore';
import { getAuth, onAuthStateChanged } from 'firebase/auth';

const GlobalContext = createContext();
export const useGlobal = () => useContext(GlobalContext);

// ── Field definitions ───────────────────────────────────────────────────────
// Each entry: { key, label, tab, check(data) → bool (true = OK) }
const REQUIRED_FIELDS = [
  // Contact Details
  { key: 'fullName',      label: 'Full name',     tab: 'Contact',      check: d => !!d.fullName?.trim() },
  { key: 'jobRole',       label: 'Job role',      tab: 'Contact',      check: d => !!d.jobRole?.trim() },
  { key: 'contactEmail',  label: 'Email address', tab: 'Contact',      check: d => !!d.contactEmail?.trim() },
  { key: 'contactPhone',  label: 'Phone number',  tab: 'Contact',      check: d => !!d.contactPhone?.trim() },

  // Club / School
  { key: 'businessName',    label: 'School / Club name', tab: 'Club · School', check: d => !!d.businessName?.trim() },
  { key: 'businessAddress', label: 'Business address',   tab: 'Club · School', check: d => !!d.businessAddress?.trim() },
  { key: 'postCode',        label: 'Post code',          tab: 'Club · School', check: d => !!d.postCode?.trim() },
  { key: 'venueAddress',    label: 'Venue address',      tab: 'Club · School', check: d => d.venues?.length > 0 && !!d.venues[0].address?.trim() },
  { key: 'venuePostCode',   label: 'Venue post code',    tab: 'Club · School', check: d => d.venues?.length > 0 && !!d.venues[0].postCode?.trim() },
  { key: 'venueContact',    label: 'Venue contact name', tab: 'Club · School', check: d => d.venues?.length > 0 && !!d.venues[0].contactName?.trim() },
  { key: 'venueEmail',      label: 'Venue contact email', tab: 'Club · School', check: d => d.venues?.length > 0 && !!d.venues[0].venueContactEmail?.trim() },
  { key: 'venuePhone',      label: 'Venue contact phone', tab: 'Club · School', check: d => d.venues?.length > 0 && !!d.venues[0].venueContactPhone?.trim() },

  // Invoice
  { key: 'legalName',    label: 'Legal name',    tab: 'Invoice', check: d => !!d.legalName?.trim() },
  { key: 'legalAddress', label: 'Legal address', tab: 'Invoice', check: d => !!d.legalAddress?.trim() },
  { key: 'invoiceEmail', label: 'Invoice email', tab: 'Invoice', check: d => !!d.invoiceEmail?.trim() },

  // Schedule
  { key: 'clubTimingsFrom',    label: 'Club open time',         tab: 'Schedule', check: d => !!d.clubTimings?.from },
  { key: 'clubTimingsTo',      label: 'Club close time',        tab: 'Schedule', check: d => !!d.clubTimings?.to },
  { key: 'lunchTimeFrom',      label: 'Lunch start time',       tab: 'Schedule', check: d => !!d.lunchTime?.from },
  { key: 'lunchTimeTo',        label: 'Lunch end time',         tab: 'Schedule', check: d => !!d.lunchTime?.to },
  { key: 'deliveryWindowFrom', label: 'Delivery window start',  tab: 'Schedule', check: d => !!d.deliveryWindow?.from },
  { key: 'deliveryWindowTo',   label: 'Delivery window end',    tab: 'Schedule', check: d => !!d.deliveryWindow?.to },
  { key: 'ageRangeLower',      label: 'Min age of children',    tab: 'Schedule', check: d => d.ageRange?.lower !== '' && d.ageRange?.lower != null },
  { key: 'ageRangeUpper',      label: 'Max age of children',    tab: 'Schedule', check: d => d.ageRange?.upper !== '' && d.ageRange?.upper != null },
];

export const computeMissingFields = (data) =>
  REQUIRED_FIELDS.filter(f => !f.check(data)).map(f => ({ key: f.key, label: f.label, tab: f.tab }));

// ── Provider ─────────────────────────────────────────────────────────────────
export const GlobalProvider = ({ children }) => {
  const [isBusinessInfoComplete, setIsBusinessInfoComplete] = useState(() => {
    const saved = localStorage.getItem('isBusinessInfoComplete');
    return saved !== null ? JSON.parse(saved) : false;
  });

  const [missingFields, setMissingFields] = useState([]);
  const [profileLoaded, setProfileLoaded] = useState(false);

  useEffect(() => {
    localStorage.setItem('isBusinessInfoComplete', JSON.stringify(isBusinessInfoComplete));
  }, [isBusinessInfoComplete]);

  // Fetch profile on auth state change and compute missing fields
  const refreshProfile = useCallback(async (uid) => {
    if (!uid) {
      setMissingFields([]);
      setProfileLoaded(true);
      return;
    }
    try {
      const snap = await getDoc(doc(getFirestore(), 'ClientContactInfo', uid));
      const data = snap.exists() ? snap.data() : {};
      const missing = computeMissingFields(data);
      setMissingFields(missing);
      const complete = missing.length === 0;
      setIsBusinessInfoComplete(complete);
      setProfileLoaded(true);
    } catch (err) {
      console.error('Error loading profile:', err);
      setProfileLoaded(true);
    }
  }, []);

  useEffect(() => {
    const unsub = onAuthStateChanged(getAuth(), (user) => {
      refreshProfile(user?.uid || null);
    });
    return () => unsub();
  }, [refreshProfile]);

  return (
    <GlobalContext.Provider value={{
      isBusinessInfoComplete,
      setIsBusinessInfoComplete,
      missingFields,
      setMissingFields,
      refreshProfile,
      profileLoaded,
    }}>
      {children}
    </GlobalContext.Provider>
  );
};
